import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    TextField, 
    Button,  
    CircularProgress, 
    FormControlLabel, 
    Checkbox, 
    MenuItem,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
    buildPermissionMap, 
    hasRight,
} from 'state/checkPermission';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import Supplier from './Supplier';


const validationSchema = yup.object().shape({
    productName: yup.string().required('Required'),
    preorderCommissionRate: yup
      .number()
      .min(0, 'Must be ≥ 0')
      .max(1, 'Must be ≤ 1')
      .typeError('Must be a valid decimal'),
    directCommissionRate: yup
      .number()
      .min(0, 'Must be ≥ 0')
      .max(1, 'Must be ≤ 1')
      .typeError('Must be a valid decimal'),
});

const initialValues = {
    productCode: '',
    productName: '',
    description: '',
    unit: '',
    price: 0,
    domesticPrice: 0,
    internationalPrice: 0,
    preorderCommissionRate: 0,
    directCommissionRate: 0,
    category: '',
    suppliers: [],
    sortOrder: 0,
    isPreorderHidden: false,
    isDirectHidden: false,
    isActive: true,
};


const ProductPage = () => {
    useAuthCheck();
    const { productId } = useParams();    
    const navigate = useNavigate();
    const user = useSelector((state) => state.user); 
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const permissionMap = useMemo(
        () => buildPermissionMap(user?.permissions),
        [user?.permissions]
    );
    const canCreate = hasRight(permissionMap, "Products", "create", false);
    const canEdit = hasRight(permissionMap, "Products", "update", false);
    const IsEditable = canCreate || canEdit;
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [productCategories, setProductCategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [formValues, setFormValues] = useState(initialValues);


    const fetchData = useCallback(async (url, setter, errorMsg) => {
        try {
            const { data } = await api.get(url);
            setter(data);
        } catch (error) {
            toast.error(error.response?.data?.message || errorMsg);
            setError(error);
        }
    }, []);

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                await Promise.all([
                    fetchData('/productcategories/active', setProductCategories, 'Failed to fetch product categories!'),
                    fetchData('/suppliers/active', setVendors, 'Failed to fetch suppliers!'),
                    productId && fetchData(`/products/${productId}`, setFormValues, 'Failed to fetch product!'),
                ]);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [fetchData, productId]);

    useEffect(() => {
        if (vendors.length > 0 && !productId) { 
            setFormValues((prev) => ({
                ...prev,
                suppliers: vendors.map((sup) => ({
                    supplierId: sup._id,
                    supplierName: sup.supplierName,
                    currency: 'VND',
                    purchasePrice: 0,
                })),
            }));
        }
    }, [vendors, productId]);

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = productId ? "PUT" : "POST";
            const url = productId
                ? `/products/update/${productId}`
                : '/products/new';

            await api({
                method,
                url,
                data: values,
            });

            toast.success(productId 
                ? "Product updated successfully." 
                : "Product created successfully.");

            // onSubmitProps.resetForm();
            navigate("/products");
        } catch (error) {
            setError(error);
            toast.error(error?.response?.data?.message || "Failed to submit form!");
        }
    };
    

    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Product"
                    subtitle={`${loading ? 'Loading...' : productId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/products")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={validationSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    gap={2}
                                    sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}
                                >                                    
                                    <TextField
                                        disabled={!IsEditable}
                                        label="Product Code"
                                        name="productCode"
                                        variant="filled"
                                        type="text"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.productCode}
                                        error={!!touched.productCode && !!errors.productCode}
                                        helperText={touched.productCode && errors.productCode}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        disabled={!IsEditable}
                                        label="Product Name"
                                        name="productName"
                                        required
                                        variant="filled"
                                        type="text"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.productName}
                                        error={!!touched.productName && !!errors.productName}
                                        helperText={touched.productName && errors.productName}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        disabled={!IsEditable}
                                        label="Description"
                                        name="description"
                                        variant="filled"
                                        multiline
                                        rows={1}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        disabled={!IsEditable}
                                        label="Unit"
                                        name="unit"
                                        select
                                        variant="filled"
                                        value={values.unit}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        helperText={touched.unit && errors.unit}
                                        error={!!touched.unit && !!errors.unit}
                                        sx={{ gridColumn: 'span 6' }}
                                    >
                                        {['Cái', 'Phần', 'Hộp', 'Gói', 'Lon', 'Chai', 'Ly', 'Cốc', 'Tép', 'Tờ'].map((unit) => (
                                            <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        disabled={!IsEditable}
                                        label="Price"
                                        name="price"
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.price}
                                        error={!!touched.price && !!errors.price}
                                        helperText={touched.price && errors.price}
                                        slotProps={{
                                            htmlInput: { min: 0, style: { textAlign: 'right' } },
                                        }}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        disabled={!IsEditable}
                                        label="Domestic Price"
                                        name="domesticPrice"
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.domesticPrice}
                                        error={!!touched.domesticPrice && !!errors.domesticPrice}
                                        helperText={touched.domesticPrice && errors.domesticPrice}
                                        slotProps={{
                                            input: {
                                                onKeyDown: (e) => {
                                                    if (e.key === '-' || e.key === 'e') {
                                                        e.preventDefault();
                                                    }
                                                },
                                            },
                                            htmlInput: { min: 0, style: { textAlign: 'right' } },
                                        }}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        disabled={!IsEditable}
                                        label="International Price"
                                        name="internationalPrice"
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.internationalPrice}
                                        error={!!touched.internationalPrice && !!errors.internationalPrice}
                                        helperText={touched.internationalPrice && errors.internationalPrice}
                                        slotProps={{
                                            input: {
                                                onKeyDown: (e) => {
                                                    if (e.key === '-' || e.key === 'e') {
                                                        e.preventDefault();
                                                    }
                                                },
                                            },
                                            htmlInput: { min: 0, style: { textAlign: 'right' } },
                                        }}
                                        sx={{ gridColumn: 'span 6' }}
                                    />
                                    <TextField
                                        disabled={!IsEditable}
                                        label="Preorder Commission Rate (%)"
                                        name="preorderCommissionRate"
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            handleChange({
                                                target: {
                                                    name: "preorderCommissionRate",
                                                    value: value ? parseFloat(value) : "", // Convert input to decimal
                                                },
                                            });
                                        }}
                                        value={values.preorderCommissionRate}
                                        error={!!touched.preorderCommissionRate && !!errors.preorderCommissionRate}
                                        helperText={touched.preorderCommissionRate && errors.preorderCommissionRate}
                                        slotProps={{
                                            htmlInput: { min: 0, max: 100, step: "0.01", style: { textAlign: 'right' } },
                                        }}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        disabled={!IsEditable}
                                        label="Direct Commission Rate (%)"
                                        name="directCommissionRate"
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            handleChange({
                                                target: {
                                                    name: "directCommissionRate",
                                                    value: value ? parseFloat(value) : "", // Convert input to decimal
                                                },
                                            });
                                        }}
                                        value={values.directCommissionRate}
                                        error={!!touched.directCommissionRate && !!errors.directCommissionRate}
                                        helperText={touched.directCommissionRate && errors.directCommissionRate}
                                        slotProps={{
                                            htmlInput: { min: 0, max: 100, step: "0.01", style: { textAlign: 'right' } },
                                        }}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        disabled={!IsEditable}
                                        label="Category"
                                        name="category"
                                        select
                                        value={values.category}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        helperText={touched.category && errors.category}
                                        error={!!touched.category && !!errors.category}
                                        variant="filled"
                                        sx={{ gridColumn: 'span 6' }}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>                                        
                                        {productCategories?.map((category) => (
                                            <MenuItem key={category._id} value={category.productCategory}>
                                                {category.description}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    
                                    <TextField
                                        disabled={!IsEditable}
                                        label="Sort Order"
                                        name="sortOrder"
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.sortOrder}
                                        error={!!touched.sortOrder && !!errors.sortOrder}
                                        helperText={touched.sortOrder && errors.sortOrder}
                                        slotProps={{
                                            input: {
                                                onKeyDown: (e) => {
                                                    if (e.key === '-' || e.key === 'e') {
                                                        e.preventDefault();
                                                    }
                                                },
                                            },
                                            htmlInput: { min: 0, style: { textAlign: 'right' } },
                                        }}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isPreorderHidden}
                                                onChange={handleChange}
                                                name="isPreorderHidden"
                                                color="primary"
                                                
                                            />
                                        }
                                        label="Is Preorder Inventory Hidden"
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isDirectHidden}
                                                onChange={handleChange}
                                                name="isDirectHidden"
                                                color="primary"
                                                
                                            />
                                        }
                                        label="Is Direct Inventory Hidden"
                                    />

                                    <FormControlLabel
                                        disabled={!IsEditable}
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                name="isActive"
                                                color="primary"
                                                
                                            />
                                        }
                                        label="Is Active"
                                    />

                                    {/* Supplier Integration */}
                                    <Box sx={{ gridColumn: 'span 12' }}>
                                        <Supplier
                                            suppliers={vendors}
                                            selectedSuppliers={values.suppliers}
                                            onSupplierChange={(updatedVendors) => setFieldValue('suppliers', updatedVendors)}
                                        />
                                    </Box>
                                                                        
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: 'span 12', height: 42 }}
                                    >
                                        {productId ? 'Update' : 'Create'}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default ProductPage;
